import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeftIcon, Button, Heading, Label, showToast } from '@quickmd-llc/design-system'
import { MEDICATIONS_PAGE_ROUTE } from '@/utils/constants'
import type { MedicationFormValues } from '../../types'
import { formatNdc } from '../../constants'
import { MedicationInfoSection } from './MedicationInfoSection'
import { DrugClassificationSection } from './DrugClassificationSection'
import { FulfillmentPartnersSection } from './FulfillmentPartnersSection'

const pricingTierSchema = z.object({
  quantity: z
    .union([z.number(), z.nan()])
    .refine((v) => !Number.isNaN(v), 'Required')
    .pipe(z.number().positive('Must be > 0')),
  patientPrice: z
    .union([z.number(), z.nan()])
    .refine((v) => !Number.isNaN(v), 'Required')
    .pipe(z.number().nonnegative('Must be ≥ 0')),
})

function createMedicationSchema(hasExistingImage: boolean) {
  return z
    .object({
      drugName: z.string().min(1, 'Medication name is required'),
      displayDescription: z.string().min(1, 'Patient-facing description is required'),
      medicationImage: z.instanceof(File).nullable(),
      gcnEnabled: z.boolean(),
      gcn: z.string(),
      ndcEnabled: z.boolean(),
      ndc: z.string(),
      otherEnabled: z.boolean(),
      otherDrugCode: z.string(),
      category: z.string().min(1, 'Category is required'),
      unit: z.string().min(1, 'Unit is required'),
      pricingTiers: z.array(pricingTierSchema).min(1, 'At least one pricing tier required'),
      isControlledSubstance: z.boolean(),
      fulfillmentPartnerIds: z.array(z.string()).min(1, 'At least one fulfillment partner is required'),
    })
    .superRefine((data, ctx) => {
      // Image required unless editing with existing image
      if (!data.medicationImage && !hasExistingImage) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Medication image is required',
          path: ['medicationImage'],
        })
      }

      // At least one drug code must be enabled
      if (!data.gcnEnabled && !data.ndcEnabled && !data.otherEnabled) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one drug code is required',
          path: ['gcnEnabled'],
        })
      }

      // GCN: 3-6 digits when enabled
      if (data.gcnEnabled && !/^\d{3,6}$/.test(data.gcn)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'GCN must be 3-6 digits',
          path: ['gcn'],
        })
      }

      // NDC: 11 digits when enabled (stored raw)
      if (data.ndcEnabled && !/^\d{11}$/.test(data.ndc)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'NDC must be exactly 11 digits',
          path: ['ndc'],
        })
      }

      // Other: 13 characters alphanumeric when enabled
      if (data.otherEnabled && !/^[A-Za-z0-9]{13}$/.test(data.otherDrugCode)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Must be exactly 13 alphanumeric characters',
          path: ['otherDrugCode'],
        })
      }
    })
}

const SECTION_1_FIELDS: Array<keyof MedicationFormValues> = ['drugName', 'medicationImage']
const SECTION_2_FIELDS: Array<keyof MedicationFormValues> = [
  'pricingTiers',
  'unit',
  'category',
  'gcnEnabled',
  'gcn',
  'ndcEnabled',
  'ndc',
  'otherEnabled',
  'otherDrugCode',
  'isControlledSubstance',
]
const SECTION_3_FIELDS: Array<keyof MedicationFormValues> = ['fulfillmentPartnerIds']

const DEFAULT_VALUES: MedicationFormValues = {
  drugName: '',
  displayDescription: '',
  medicationImage: null,
  gcnEnabled: false,
  gcn: '',
  ndcEnabled: false,
  ndc: '',
  otherEnabled: false,
  otherDrugCode: '',
  category: '',
  unit: 'Tablets',
  pricingTiers: [{ quantity: 30, patientPrice: 0 }],
  isControlledSubstance: false,
  fulfillmentPartnerIds: [],
}

interface MedicationFormPageProps {
  mode: 'add' | 'edit'
  defaultValues?: Partial<MedicationFormValues>
  existingImageUrl?: string | null
  medicationId?: string
  successMessage: string
  onSubmit: (values: MedicationFormValues) => Promise<string | undefined>
  isPending: boolean
}

export function MedicationFormPage({
  mode,
  defaultValues,
  existingImageUrl,
  medicationId,
  successMessage,
  onSubmit,
  isPending,
}: MedicationFormPageProps) {
  const navigate = useNavigate()

  function navigateToList(selectId?: string | null) {
    const id = selectId ?? medicationId
    const params = id ? `?selected=${id}` : ''
    navigate(`${MEDICATIONS_PAGE_ROUTE}${params}`)
  }

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    trigger,
    formState: { errors, touchedFields, isSubmitted },
  } = useForm<MedicationFormValues>({
    resolver: zodResolver(createMedicationSchema(Boolean(existingImageUrl))),
    defaultValues: { ...DEFAULT_VALUES, ...defaultValues },
    mode: 'onTouched',
  })

  function getSectionState(fields: Array<keyof MedicationFormValues>) {
    const anyTouched = fields.some((f) => {
      const val = touchedFields[f]
      // For array fields like pricingTiers, check if any element is touched
      if (Array.isArray(val)) return val.length > 0
      return !!val
    })
    const hasError = fields.some((f) => !!errors[f])
    return {
      showIndicator: anyTouched || isSubmitted,
      isValid: !hasError,
    }
  }

  const section1State = getSectionState(SECTION_1_FIELDS)
  const section2State = getSectionState(SECTION_2_FIELDS)
  const section3State = getSectionState(SECTION_3_FIELDS)

  async function handleFormSubmit(values: MedicationFormValues) {
    const id = await onSubmit(values)
    showToast({ type: 'success', message: successMessage, position: 'bottom-right' })
    navigateToList(id ?? null)
  }

  const title = mode === 'add' ? 'Add medication' : 'Edit medication'

  const pageContent = (
    <div style={{ width: '100%' }}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className='grid h-lvh [grid-template-rows:1fr_auto]'>
        {/* Scrollable area: header + sections in one padded container */}
        <div className='overflow-y-auto bg-background p-sp-6'>
          <div>
            {/* Back + title */}
            <button
              type='button'
              onClick={() => navigateToList()}
              className='mb-sp-0.5 flex items-center gap-sp-0.5 text-text-dark'>
              <ArrowLeftIcon iconSize='s' />
              <Label size='md'>Back</Label>
            </button>
            <Heading level='h1'>{title}</Heading>
          </div>

          {/* Form sections */}
          <div className='mt-sp-3 flex flex-col gap-sp-3'>
            {/*
             // @ts-expect-error TS2375 -- MIGRATION: not approved, fix when encountered */}
            <MedicationInfoSection
              register={register}
              control={control}
              errors={errors}
              setValue={setValue}
              showIndicator={section1State.showIndicator}
              isValid={section1State.isValid}
              existingImageUrl={existingImageUrl}
              touchedFields={touchedFields}
              isSubmitted={isSubmitted}
            />
            <DrugClassificationSection
              register={register}
              control={control}
              errors={errors}
              watch={watch}
              setValue={setValue}
              clearErrors={clearErrors}
              trigger={trigger}
              showIndicator={section2State.showIndicator}
              isValid={section2State.isValid}
              touchedFields={touchedFields}
              isSubmitted={isSubmitted}
              formatNdc={formatNdc}
            />
            <FulfillmentPartnersSection
              watch={watch}
              setValue={setValue}
              trigger={trigger}
              errors={errors}
              showIndicator={section3State.showIndicator}
              isValid={section3State.isValid}
              touchedFields={touchedFields}
              isSubmitted={isSubmitted}
            />
          </div>
        </div>

        {/* Footer */}
        <div className='flex justify-end gap-sp-1 border-t border-border-dark bg-white px-sp-3 py-sp-2'>
          <Button type='button' priority='secondary' onClick={() => navigateToList()}>
            Cancel
          </Button>
          <Button type='submit' loading={isPending}>
            Save medication
          </Button>
        </div>
      </form>
    </div>
  )

  return pageContent
}
